import { OfferScope, OfferType, type StorefrontActiveOffer } from '../types/offer.types';

export const coerceNumber = (v: unknown): number => {
  if (v == null) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') return Number(v) || 0;
  if (typeof v === 'object' && v !== null && 'd' in (v as any)) {
    try {
      const s = (v as any).toString?.();
      return Number(s) || 0;
    } catch {
      return 0;
    }
  }
  return Number(v as any) || 0;
};

export const computeSavingPerUnit = (
  price: number,
  offer: StorefrontActiveOffer | null | undefined
): number => {
  if (!offer) return 0;
  const raw =
    offer.discountType === OfferType.PERCENTAGE_DISCOUNT
      ? (price * coerceNumber(offer.discountValue)) / 100
      : coerceNumber(offer.discountValue);
  // maxDiscountAmount == 0 / null / '' means no cap — evitar Math.min(...,0) que anularía descuento
  const rawCap = offer.maxDiscountAmount;
  const cap =
    rawCap == null || rawCap === '' || Number(rawCap) === 0
      ? Infinity
      : coerceNumber(rawCap) || Infinity;
  return Math.min(raw, cap, price);
};

export const computeDiscountedPrice = (
  price: number,
  offer: StorefrontActiveOffer | null | undefined
): number => {
  const saving = computeSavingPerUnit(price, offer);
  return Math.max(0, price - saving);
};

/**
 * Oferta aplica SOLO al neto del producto (product.price), NO a modifiers/extras.
 * Para no romper display legacy, mantenemos computeDiscountedPrice(productPrice+extras) deprecado
 * y exponemos este helper explícito para unit con modifiers.
 */
export const computeSavingPerUnitOnBase = (
  basePrice: number,
  offer: StorefrontActiveOffer | null | undefined
): number => computeSavingPerUnit(basePrice, offer);

export const computeDiscountedUnitPrice = (
  basePrice: number,
  extrasUnitPrice: number,
  offer: StorefrontActiveOffer | null | undefined
): number => {
  const discountedBase = computeDiscountedPrice(basePrice, offer);
  return discountedBase + (extrasUnitPrice || 0);
};

export const computeDiscountedBasePrice = (
  basePrice: number,
  offer: StorefrontActiveOffer | null | undefined
): number => computeDiscountedPrice(basePrice, offer);

export const formatOfferBadge = (
  offer: StorefrontActiveOffer | null | undefined
): string | null => {
  if (!offer) return null;
  if (offer.discountType === OfferType.PERCENTAGE_DISCOUNT)
    return `-${coerceNumber(offer.discountValue)}%`;
  return `-$${coerceNumber(offer.discountValue).toFixed(0)}`;
};

export const formatOfferBadgeLong = (
  offer: StorefrontActiveOffer | null | undefined
): string | null => {
  if (!offer) return null;
  if (offer.discountType === OfferType.PERCENTAGE_DISCOUNT)
    return `-${coerceNumber(offer.discountValue)}% OFF`;
  return `-$${coerceNumber(offer.discountValue).toFixed(2)} OFF`;
};

export const isGlobalScope = (offer: StorefrontActiveOffer | null | undefined): boolean =>
  offer?.discountScope === OfferScope.ALL_PRODUCTS;

export const offerLeyend = (offer: StorefrontActiveOffer | null | undefined): string | null => {
  if (!offer) return null;
  if (isGlobalScope(offer)) return 'Oferta en toda la tienda';
  if (offer.discountScope === OfferScope.CATEGORY) return 'Oferta en categoría';
  if (offer.discountScope === OfferScope.PRODUCT) return offer.name || 'Oferta';
  return null;
};

export const getOfferTypeLabel = (type: OfferType): string =>
  type === OfferType.PERCENTAGE_DISCOUNT ? '% Porcentaje' : 'Monto fijo';

export const getOfferScopeLabel = (scope: OfferScope): string => {
  switch (scope) {
    case OfferScope.PRODUCT:
      return 'Producto';
    case OfferScope.CATEGORY:
      return 'Categoría';
    case OfferScope.ALL_PRODUCTS:
      return 'Toda la tienda';
    case OfferScope.ORDER:
      return 'Orden';
    default:
      return scope;
  }
};
